import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';

const EntraCallbackPage = () => {
  const navigate = useNavigate();
  const search = useSearch({ from: '/auth/entra-callback' });
  const { loginWithEntra } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [isAuthenticating, setIsAuthenticating] = useState(true);

  useEffect(() => {
    const PROCESSING_KEY = 'entra_processing';
    const CODE_KEY = 'entra_code';
    
    // Check if already processing (across component mounts)
    const alreadyProcessing = sessionStorage.getItem(PROCESSING_KEY);
    if (alreadyProcessing === 'true') {
      console.log('Skipping - already processing in another instance');
      return;
    }

    const handleCallback = async () => {
      // Get the authorization code from URL or sessionStorage
      let code = sessionStorage.getItem(CODE_KEY);
      if (!code) {
        code = (search as any).code;
        if (code) {
          // Store it for subsequent renders
          sessionStorage.setItem(CODE_KEY, code);
          
          // Remove from URL immediately
          try {
            const url = new URL(window.location.href);
            url.searchParams.delete('code');
            url.searchParams.delete('state');
            window.history.replaceState({}, document.title, url.toString());
          } catch {}
        }
      }
      
      if (!code) {
        console.error('No authorization code found');
        setError('No authorization code received from Microsoft');
        setIsAuthenticating(false);
        setTimeout(() => navigate({ to: '/' }), 3000);
        return;
      }

      // Mark as processing (across component mounts)
      sessionStorage.setItem(PROCESSING_KEY, 'true');
      console.log('Processing Entra callback with code:', code.substring(0, 10) + '...');

      try {
        const success = await loginWithEntra(code);
        console.log('loginWithEntra returned:', success);

        if (success) {
          // Authentication successful - clean up and navigate
          console.log('Authentication successful, transitioning to success state');
          sessionStorage.removeItem(CODE_KEY);
          sessionStorage.removeItem(PROCESSING_KEY);
          setIsAuthenticating(false);
          
          setTimeout(() => {
            console.log('Navigating to dashboard');
            navigate({ to: '/dashboard' });
          }, 1000);
        } else {
          console.error('Authentication failed - success was false');
          sessionStorage.removeItem(CODE_KEY);
          sessionStorage.removeItem(PROCESSING_KEY);
          setError('Authentication failed. Please try again.');
          setIsAuthenticating(false);
          setTimeout(() => navigate({ to: '/' }), 3000);
        }
      } catch (err: any) {
        console.error('Entra callback error:', err);
        sessionStorage.removeItem(CODE_KEY);
        sessionStorage.removeItem(PROCESSING_KEY);
        
        // Ignore "already processed" errors
        if (!err.message?.includes('already processed')) {
          setError(err.message || 'Authentication failed');
          setIsAuthenticating(false);
          setTimeout(() => navigate({ to: '/' }), 3000);
        } else {
          // Code was already processed successfully elsewhere, just navigate
          console.log('Code already processed, waiting for redirect...');
          setTimeout(() => {
            navigate({ to: '/dashboard' });
          }, 500);
        }
      }
    };

    handleCallback();
    
    // Cleanup function
    return () => {
      console.log('Cleanup: Component unmounting');
    };
  }, [search, loginWithEntra, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--color-bg-secondary)' }}>
      <div className="card max-w-md w-full p-8 text-center">
        {error ? (
          <>
            <div className="text-red-500 mb-4">
              <svg className="w-16 h-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-xl font-bold mb-2" style={{ color: 'var(--color-text-primary)' }}>
              Authentication Failed
            </h2>
            <p style={{ color: 'var(--color-text-secondary)' }}>{error}</p>
            <p className="text-sm mt-4" style={{ color: 'var(--color-text-muted)' }}>
              Redirecting to login page...
            </p>
          </>
        ) : isAuthenticating ? (
          <>
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 mx-auto mb-4" style={{ borderColor: 'var(--color-accent)' }}></div>
            <h2 className="text-xl font-bold mb-2" style={{ color: 'var(--color-text-primary)' }}>
              Authenticating...
            </h2>
            <p style={{ color: 'var(--color-text-secondary)' }}>
              Please wait while we complete your sign-in
            </p>
          </>
        ) : (
          <>
            <div className="text-green-500 mb-4">
              <svg className="w-16 h-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-xl font-bold mb-2" style={{ color: 'var(--color-text-primary)' }}>
              Authentication Successful
            </h2>
            <p style={{ color: 'var(--color-text-secondary)' }}>
              Redirecting to dashboard...
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export const Route = createFileRoute('/auth/entra-callback')({
  component: EntraCallbackPage,
});

